import {
  Box,
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { FieldArray, Form, Formik, useField, useFormikContext } from "formik";
import { type ReactNode } from "react";
import { RiAddLine, RiArrowDownSLine } from "react-icons/ri";
import { GenericError } from "src/components/Feedback/GenericError";
import { FormikSwitchInput } from "src/components/Inputs/FormikSwitchInput";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { ResizableDrawer } from "src/components/Layout/ResizableDrawer";
import {
  NavigationBlocker,
  useNavigationBlockerContext,
} from "src/components/NavigationBlocker";
import { SegmentedControl } from "src/components/SegmentedControl";
import {
  listDataPoints,
  type CriteriaSet,
  type EligibilityDataPoint,
} from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useRemoteDataRestQuery } from "src/hooks/useRemoteDataQuery";
import * as RD from "src/types/remoteData";
import { useEnrollmentPeriodContext } from "../../Edit";
import { AddressConditionCard } from "./AddressConditionCard";
import { AnswerSelectionConditionCard } from "./AnswerSelectionConditionCard";
import { CapacityConditionCard } from "./CapacityConditionCard";
import {
  InheritedConditionsDescriptions,
  PanelLoading,
} from "./ConditionsDescription";
import { DateConditionCard } from "./DateConditionCard";
import { emptyAddressCondition } from "./addressCondition";
import { emptyAnswerSelection } from "./answerSelection";
import { emptyCapacityCondition } from "./capacityCondition";
import {
  formValuesToConditionsObject,
  validateConditionsForm,
  type ConditionsFormValues,
  type MatchMode,
} from "./conditionsForm";
import {
  type CriterionForEntity,
  type InheritedCriterionSource,
} from "./criterionForEntity";
import { emptyDateCondition } from "./dateCondition";

type EditConditionsProp = {
  enrollmentPeriodId: string;
  entity: CriterionForEntity;
  criteriaSet: CriteriaSet;
  onDone: () => void;
};

const MATCH_MODE_COPY: Record<MatchMode, string> = {
  all: "Requires all conditions to be true to pass.",
  any: "Pass if any condition is true.",
};

export function EditConditions({
  enrollmentPeriodId,
  entity,
  criteriaSet,
  onDone,
}: EditConditionsProp) {
  const toast = useAvelaToast();
  const queryClient = useQueryClient();
  const { enrollmentPeriod } = useEnrollmentPeriodContext();
  const useSeatTypes = enrollmentPeriod.use_service_offering_seat_types;
  const criterionRD = useRemoteDataRestQuery({
    queryKey: entity.queryKey(criteriaSet.id),
    queryFn: () => entity.fetch(criteriaSet.id),
  });
  const dataPointsRD = useRemoteDataRestQuery({
    queryKey: ["ListDataPoints", enrollmentPeriodId],
    queryFn: () =>
      listDataPoints({ path: { enrollmentPeriodId } }).then(
        (data) => data.data?.data ?? []
      ),
  });
  const inheritedSources = entity.inheritedSources ?? [];

  const handleSubmit = async (values: ConditionsFormValues) => {
    try {
      await entity.upsert(criteriaSet.id, {
        conditions: formValuesToConditionsObject(values),
        ...(typeof values.inheritConditions === "boolean"
          ? { inheritConditions: values.inheritConditions }
          : {}),
      });
      toast({
        id: "save-criterion",
        title: "Conditions saved",
      });
      await queryClient.invalidateQueries({
        queryKey: entity.queryKey(criteriaSet.id),
      });
      onDone();
    } catch (err) {
      console.error(err);
      toast.error({
        id: "save-criterion",
        title: "Failed to save conditions",
      });
    }
  };

  return (
    <RemoteDataView
      remoteData={criterionRD}
      loading={
        <EditConditionsFrame onDone={onDone}>
          {entity.description(criteriaSet.name)}
          <PanelLoading />
        </EditConditionsFrame>
      }
      error={(error) => (
        <EditConditionsFrame onDone={onDone}>
          {entity.description(criteriaSet.name)}
          <GenericError error={error} />
        </EditConditionsFrame>
      )}
    >
      {(criterion) => (
        <Formik<ConditionsFormValues>
          key={criteriaSet.id}
          initialValues={{
            ...criterion.formValues,
            ...(inheritedSources.length > 0
              ? {
                  inheritConditions:
                    criterion.criterion?.inheritConditions ?? true,
                }
              : {}),
          }}
          onSubmit={handleSubmit}
          validate={validateConditionsForm}
        >
          {() => (
            <ResizableDrawer.Content as={Form} noValidate>
              <ResizableDrawer.Header>Criteria settings</ResizableDrawer.Header>
              <ResizableDrawer.Body>
                <VStack align="stretch" spacing={6}>
                  {entity.description(criteriaSet.name)}
                  <ConditionsForm
                    enrollmentPeriodId={enrollmentPeriodId}
                    criteriaSetName={criteriaSet.name}
                    useSeatTypes={useSeatTypes}
                    dataPoints={dataPointsRD}
                    inheritedSources={inheritedSources}
                    criteriaSetId={criteriaSet.id}
                  />
                </VStack>
              </ResizableDrawer.Body>
              <ResizableDrawer.Footer>
                <EditConditionsFooter onDone={onDone} />
              </ResizableDrawer.Footer>
            </ResizableDrawer.Content>
          )}
        </Formik>
      )}
    </RemoteDataView>
  );
}

function EditConditionsFrame({
  onDone,
  children,
}: {
  onDone: () => void;
  children: ReactNode;
}) {
  return (
    <ResizableDrawer.Content>
      <ResizableDrawer.Header>Criteria settings</ResizableDrawer.Header>
      <ResizableDrawer.Body>
        <VStack align="stretch" spacing={6}>
          {children}
        </VStack>
      </ResizableDrawer.Body>
      <ResizableDrawer.Footer>
        <Button variant="outline" colorScheme="gray" onClick={onDone}>
          Back
        </Button>
        <Button colorScheme="primary" isDisabled>
          Save
        </Button>
      </ResizableDrawer.Footer>
    </ResizableDrawer.Content>
  );
}

function EditConditionsFooter({ onDone }: { onDone: () => void }) {
  const { confirm } = useNavigationBlockerContext();
  const { dirty, isSubmitting, isValid } =
    useFormikContext<ConditionsFormValues>();
  const canSave = dirty && isValid;

  const handleBack = async () => {
    if (!dirty || (await confirm())) onDone();
  };

  return (
    <>
      <Button
        variant="outline"
        colorScheme="gray"
        onClick={() => {
          void handleBack();
        }}
        isDisabled={isSubmitting}
      >
        Back
      </Button>
      <Button
        type="submit"
        colorScheme="primary"
        isDisabled={!canSave}
        isLoading={isSubmitting}
      >
        Save
      </Button>
    </>
  );
}

function ConditionsForm({
  enrollmentPeriodId,
  criteriaSetName,
  useSeatTypes,
  dataPoints,
  inheritedSources,
  criteriaSetId,
}: {
  enrollmentPeriodId: string;
  criteriaSetName: string;
  useSeatTypes: boolean;
  dataPoints: RD.RemoteData<Error, EligibilityDataPoint[]>;
  inheritedSources: readonly InheritedCriterionSource[];
  criteriaSetId: string;
}) {
  const { dirty, values } = useFormikContext<ConditionsFormValues>();
  const [matchModeField, , matchModeHelpers] = useField<MatchMode>("matchMode");

  return (
    <VStack align="stretch">
      <NavigationBlocker hasChanges={dirty} />
      {values.inheritConditions !== undefined && (
        <VStack align="stretch" spacing={4} mb={6}>
          <FormikSwitchInput<boolean>
            name="inheritConditions"
            label="Inherit conditions"
            direction="row"
            checked={[true, ""]}
            unchecked={[false, ""]}
            formLabelProps={{ mb: 0 }}
          />
          <Text variant="helperText">
            {inheritConditionsDescription({
              enabled: values.inheritConditions,
              useSeatTypes,
              criteriaSetName,
            })}
          </Text>
          {values.inheritConditions && (
            <InheritedConditionsDescriptions
              sources={inheritedSources}
              criteriaSetId={criteriaSetId}
            />
          )}
        </VStack>
      )}
      <VStack align="stretch" spacing={2}>
        {inheritedSources.length > 0 && (
          <Text fontSize="xs" fontWeight="bold" color="gray.600" mb={1}>
            Conditions specific to this{" "}
            {useSeatTypes ? "seat type" : "offering"}
          </Text>
        )}
        <VStack align="stretch" spacing={2}>
          <Box alignSelf="center" w="10rem">
            <SegmentedControl<MatchMode>
              options={[
                { label: "All of", value: "all" },
                { label: "Any of", value: "any" },
              ]}
              value={matchModeField.value}
              onChange={matchModeHelpers.setValue}
              radioProps={{
                fontSize: "xs",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
                px: 2,
              }}
            />
          </Box>
          <Text variant="helperText" textAlign="center">
            {MATCH_MODE_COPY[matchModeField.value]}
          </Text>
        </VStack>
        <FieldArray name="conditions">
          {(helper) => (
            <VStack align="stretch" spacing={2}>
              {values.conditions.map((condition, index) => {
                const fieldName = `conditions.${index}`;
                const onDelete = () => helper.remove(index);
                if (condition.kind === "date") {
                  return (
                    <DateConditionCard
                      key={condition.id}
                      name={fieldName}
                      enrollmentPeriodId={enrollmentPeriodId}
                      dataPoints={dataPoints}
                      onDelete={onDelete}
                    />
                  );
                }
                if (condition.kind === "address") {
                  return (
                    <AddressConditionCard
                      key={condition.id}
                      name={fieldName}
                      enrollmentPeriodId={enrollmentPeriodId}
                      dataPoints={dataPoints}
                      onDelete={onDelete}
                    />
                  );
                }
                if (condition.kind === "capacity") {
                  return (
                    <CapacityConditionCard
                      key={condition.id}
                      name={fieldName}
                      onDelete={onDelete}
                    />
                  );
                }
                if (condition.kind === "answerSelection") {
                  return (
                    <AnswerSelectionConditionCard
                      key={condition.id}
                      name={fieldName}
                      enrollmentPeriodId={enrollmentPeriodId}
                      dataPoints={dataPoints}
                      onDelete={onDelete}
                    />
                  );
                }
                return null;
              })}
              <Box alignSelf="flex-start">
                <Menu>
                  <MenuButton
                    as={Button}
                    variant="outline"
                    colorScheme="gray"
                    size="sm"
                    leftIcon={<RiAddLine />}
                    rightIcon={<RiArrowDownSLine />}
                  >
                    Add condition
                  </MenuButton>
                  <MenuList>
                    <MenuItem onClick={() => helper.push(emptyDateCondition())}>
                      Date
                    </MenuItem>
                    <MenuItem
                      onClick={() => helper.push(emptyAddressCondition())}
                    >
                      Address
                    </MenuItem>
                    <MenuItem
                      onClick={() => helper.push(emptyCapacityCondition())}
                    >
                      Capacity
                    </MenuItem>
                    <MenuItem
                      onClick={() => helper.push(emptyAnswerSelection())}
                    >
                      Answer selection
                    </MenuItem>
                  </MenuList>
                </Menu>
              </Box>
            </VStack>
          )}
        </FieldArray>
      </VStack>
    </VStack>
  );
}

function inheritConditionsDescription({
  enabled,
  useSeatTypes,
  criteriaSetName,
}: {
  enabled: boolean;
  useSeatTypes: boolean;
  criteriaSetName: string;
}) {
  const subject = useSeatTypes ? "seat type" : "offering";
  if (enabled) {
    return `Require every set of conditions from the school and options of this ${subject} to be met for it to pass ${criteriaSetName}.`;
  }
  return `Do not consider the conditions from the school and options of this ${subject} when evaluating it for ${criteriaSetName}.`;
}
